import React, { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import cn from 'classnames';

import { Button, Checkbox, Container, Icon, Icons, InputText, RadioGroup, yup, yupResolver } from '@dansk-metal/ui';

import { UmbracoLink } from '@web/components/umbraco-link/umbraco-link';

import { validationMessages } from '@web/constants/messages/validation-messages';

import { useAuthenticatedCRMRequestConfig } from '@web/services/crm-api/hooks';
import { ContributionDto, PaymentForm, putPaymentSession, putPbsPayment } from '@web/services/crm-api/rest';
import { Link as UmbracoLinkType } from '@web/services/umbraco/types/basic/Link';

import styles from './user-payment-form.module.scss';

export type UserPaymentFormProps = {
	onSubmitSuccess: Function;
	paymentMethod: string;
	membershipInfoUrl: UmbracoLinkType;
	memberContributions?: ContributionDto | void | null;
};

const schema = yup.object({
	registrationNumber: yup.string().when('paymentOptions.Pbs', {
		is: (Pbs: boolean) => Pbs,
		then: (rule) => rule.matches(/^\d{4}$/, validationMessages.payment_reg_nr.error).required(validationMessages.payment_reg_nr.empty),
	}),
	accountNumber: yup.string().when('paymentOptions.Pbs', {
		is: (Pbs: boolean) => Pbs,
		then: (rule) => rule.matches(/^\d{10}$/, validationMessages.payment_konto_nr.error).required(validationMessages.payment_konto_nr.empty),
	}),
	acceptTerms: yup.boolean().when('paymentOptions.Pbs', {
		is: (Pbs: boolean) => Pbs,
		then: (rule) => rule.oneOf([true], 'Vilkår og betingelser skal accepteres'),
	}),
	paymentOptions: yup.object({
		Girokort: yup.boolean(),
		MobilePay: yup.boolean(),
		Card: yup.boolean(),
		Pbs: yup.boolean(),
	}),
});

export function UserPaymentForm({
	onSubmitSuccess,
	paymentMethod,
	membershipInfoUrl,
	memberContributions,
}: UserPaymentFormProps) {
	const { control, handleSubmit, setValue, getValues } = useForm({
		defaultValues: {
			registrationNumber: '',
			accountNumber: '',
			acceptTerms: false,
			paymentOptions: {
				Girokort: false,
				MobilePay: false,
				Card: false,
				Pbs: false,
			},
		},
		resolver: yupResolver(schema),
		mode: 'onSubmit',
		reValidateMode: 'onSubmit',
	});

	const { memberId, requestConfig } = useAuthenticatedCRMRequestConfig();
	const [options, setOptions] = useState([{ value: '', label: '' }]);
	const [showUpdateCardButton, setShowUpdateCardButton] = useState(false);
	const [disableChangePaymentButton, setDisableChangePaymentButton] = useState(false);
	const [showUpdatePaymentServiceForm, setShowUpdatePaymentServiceForm] = useState(false);
	const [salaryDeducted, setSalaryDeducted] = useState(false);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [companyPayment, setCompanyPayment] = useState(false);
	const [partialCompanyPayment, setPartialCompanyPayment] = useState(false);

	const handlePaymentData = (paymentData) => {
		if (paymentData.paymentOptions.MobilePay) {
			return PaymentForm.MobilePay;
		}

		return PaymentForm.Card;
	};

	const onSubmit = async (paymentData) => {
		setDisableChangePaymentButton(true);
		setIsSubmitting(true);
		try {
			if (paymentData.paymentOptions.Pbs) {
				const params = {
					reg: paymentData.registrationNumber,
					konto: paymentData.accountNumber,
				};
				await putPbsPayment(memberId, params, requestConfig);
				setIsSubmitting(false);
				onSubmitSuccess(paymentData);
				setDisableChangePaymentButton(false);
			} else {
				const params = {
					cancelUrl: `${window.location.origin}/api/crm/payment/callback?return=${encodeURI(window.location.href)}`,
					redirectUrl: `${window.location.origin}/api/crm/payment/callback?return=${encodeURI(window.location.href)}&paymentMethod=${handlePaymentData(paymentData)}`,
					paymentMethod: handlePaymentData(paymentData),
				};

				const session = await putPaymentSession(memberId, params, requestConfig);
				setIsSubmitting(false);
				if (session) {
					window.location.href = session.sessionUrl;
				}
			}
		} catch (catchError) {
			console.error(catchError);
			setDisableChangePaymentButton(false);
		}
	};

	const onUpdatePaymentMethod = () => {
		if (getValues('paymentOptions').Pbs) {
			setShowUpdatePaymentServiceForm(true);
		}

		return false;
	};

	const handleBackButton = () => {
		setValue('registrationNumber', '');
		setValue('accountNumber', '');
		setValue('acceptTerms', false);
		setShowUpdatePaymentServiceForm(false);
	};

	useEffect(() => {
		const updatedOptions = determinePaymentOptions(paymentMethod);

		setOptions(updatedOptions);

		const paymentOptions = getValues('paymentOptions');
		Object.keys(paymentOptions).forEach((key) => {
			paymentOptions[key] = key === paymentMethod;
		});
		const disableButton = shouldDisableChangePaymentButton(paymentOptions, paymentMethod);
		setDisableChangePaymentButton(disableButton);
		setValue('paymentOptions', paymentOptions);
		setSalaryDeducted(paymentMethod === 'firmatræk');
		setShowUpdateCardButton(paymentMethod === 'Card' && getValues('paymentOptions').Card);
	}, [paymentMethod, setValue, getValues]);

	const handleChange = (e) => {
		const targetValue = e.target.value;
		const paymentOptions = getValues('paymentOptions');

		Object.keys(paymentOptions).forEach((key) => {
			paymentOptions[key] = key === targetValue;
			return paymentOptions;
		});

		const disableButton = shouldDisableChangePaymentButton(paymentOptions, paymentMethod);
		setDisableChangePaymentButton(disableButton);

		setValue('paymentOptions', paymentOptions);
		setShowUpdateCardButton(paymentMethod === 'Card' && getValues('paymentOptions').Card);
	};

	function shouldDisableChangePaymentButton(paymentOptions, currentPaymentMethod) {
		return Object.keys(paymentOptions).some(
			(key) => paymentOptions[key] && key === currentPaymentMethod,
		);
	}

	function determinePaymentOptions(payment) {
		let updatedOptions: { value: string; label: string; icon?: Icons }[] = [];

		switch (payment) {
			case 'MobilePay':
				updatedOptions = [
					{ value: 'MobilePay', label: 'MobilePay', icon: 'payment-mobile-pay' },
					{ value: 'Card', label: 'Betalingskort', icon: 'payment-cards' },
					{ value: 'Pbs', label: 'Betalingsservice', icon: 'payment-service' },
				];
				break;
			case 'Card':
				updatedOptions = [
					{ value: 'Card', label: 'Betalingskort', icon: 'payment-cards' },
					{ value: 'MobilePay', label: 'MobilePay', icon: 'payment-mobile-pay' },
					{ value: 'Pbs', label: 'Betalingsservice', icon: 'payment-service' },
				];
				break;
			case 'Pbs':
				updatedOptions = [
					{ value: 'Pbs', label: 'Betalingsservice', icon: 'payment-service' },
					{ value: 'MobilePay', label: 'MobilePay', icon: 'payment-mobile-pay' },
					{ value: 'Card', label: 'Betalingskort', icon: 'payment-cards' },
				];
				break;
			case 'Girokort':
				updatedOptions = [
					{ value: 'Girokort', label: 'Girokort' },
					{ value: 'MobilePay', label: 'MobilePay', icon: 'payment-mobile-pay' },
					{ value: 'Card', label: 'Betalingskort', icon: 'payment-cards' },
					{ value: 'Pbs', label: 'Betalingsservice', icon: 'payment-service' },
				];
				break;
			default:
				console.warn('Invalid payment option:', payment);
				break;
		}

		return updatedOptions;
	}

	useEffect(() => {
		if (memberContributions) {
			const employerPayment = memberContributions.contributionPaidByEmployer;
			const memberPayment = memberContributions.contributionPaidByMember;

			if (employerPayment && memberPayment) {
				setPartialCompanyPayment(true);
			} else if (employerPayment && !memberPayment) {
				setCompanyPayment(true);
			}
		}
	}, [companyPayment, memberContributions]);

	function renderFormContent() {
		if (showUpdatePaymentServiceForm) {
			return (
				<Container className={styles.container}>
					<form onSubmit={handleSubmit(onSubmit)}>
						<div>
							<div className={styles.form_container}>
								<button className={styles.button} onClick={handleBackButton}>
									<Icon icon="arrow-left-color" />
									<p className={styles.back_link}>Tilbage</p>
								</button>
								<div className={styles.input_container}>
									<h4 className={styles.form_header}>Oplys dine kontooplysninger</h4>
									<Controller
										control={control}
										name="registrationNumber"
										render={({ field, fieldState }) => (
											<InputText fieldState={fieldState} disabled={isSubmitting} {...field} noOptionalLabel label="Reg. nr." />
										)}
									/>
									<Controller
										control={control}
										name="accountNumber"
										render={({ field, fieldState }) => (
											<InputText fieldState={fieldState} disabled={isSubmitting} {...field} noOptionalLabel label="Kontonr." />
										)}
									/>
									<Controller
										control={control}
										name="acceptTerms"
										render={({ field, fieldState }) => (
											<div className={styles.checkbox}>
												<Checkbox
													{...field}
													disabled={isSubmitting}
													fieldState={fieldState}
													required
													label={
														<p className={styles.info_text}>
															Jeg accepterer
															<UmbracoLink className={styles.link} link={membershipInfoUrl}>
																medlemsbetingelserne
															</UmbracoLink>
														</p>
													}
												/>
											</div>
										)}
									/>
								</div>
							</div>
							<div className={styles.button_container}>
								<Button type="submit" disabled={disableChangePaymentButton || isSubmitting} icon={{ icon: isSubmitting ? 'spinner' : 'checkmark' }}>
									Gem kontooplysninger
								</Button>
							</div>
						</div>
					</form >
				</Container>
			);
		}

		return (
			<Container className={styles.container}>
				<form onSubmit={handleSubmit(onSubmit)}>
					{partialCompanyPayment
						&& (
							<div className={cn(styles.info_container, styles.info_container__alt)}>
								<strong>Dit kontingent betales delvist over din løn</strong>
								<p className={styles.description_text}>Her på siden skifter du kun betalingsoplysninger for
									den del af dit kontingent, som du selv betaler.
								</p>
							</div>
						)}
					{companyPayment
						? (
							<div className={styles.info_container}>
								<strong>Dit kontingent betales via løntræk</strong>
								<p className={styles.description_text}>
									For at skifte betalingsmetode skal du kontakte enten din tillidsrepræsentant eller lokalafdeling.
								</p>
							</div>
						) : (
							<>
								<div className={styles.form_container}>
									<Controller
										control={control}
										name="paymentOptions"
										render={({ field, fieldState }) => (
											<RadioGroup
												className={styles.radio_buttons}
												{...field}
												fieldState={fieldState}
												options={options}
												onChange={handleChange}
												disabled={isSubmitting}
												noOptionalLabel
											/>
										)}
									/>
								</div>
								<div className={styles.button_container}>
									{showUpdateCardButton ? (
										<Button icon={{ icon: isSubmitting ? 'spinner' : 'arrow-right' }} type="submit" disabled={isSubmitting}>
											Opdatér betalingskort
										</Button>
									) : (
										<Button icon={{ icon: isSubmitting ? 'spinner' : 'arrow-right' }} type="submit" onClick={onUpdatePaymentMethod} disabled={disableChangePaymentButton || isSubmitting}>
											Skift betalingsmetode
										</Button>
									)}
								</div>
							</>
						)}
				</form>
			</Container>
		);
	}

	return (
		<Container className={styles.container}>
			{salaryDeducted ? (
				<div className={styles.salary_container}>
					<p className={styles.header}>Dit kontingent betales over din løn</p>
					<p>
						For at skifte betalingsmetode skal du kontakte din klubkasserer,.
						Er du i tvivl om hvem det er, skal du kontakte din arbejdsgiver.
					</p>
				</div>
			) : (
				renderFormContent()
			)}
		</Container>
	);
}
